// ============================================================================
// PocManagerModal — Model B (2026-06-09) point-of-contact manager.
//
// Manages the contact_assignments for a facility (and its account-wide pool):
//   - list every assignment (displayed + hidden), ordered by display_order
//   - toggle is_displayed   ("Show on card" / "Hide" — hide == remove, since
//                            the canonical framework blocks hard deletes)
//   - set is_primary        (single primary per facility/account scope)
//   - reorder               (display_order, up/down within the displayed set)
//   - edit per-assignment    contact_role / job_title / phone / email overrides
//   - add an existing person (SmartSelect over the contacts person-master)
//   - add a brand-new person (creates a contacts master + a scoped assignment)
//
// Cards display the first ~5 is_displayed rows; this manager surfaces a cap
// warning past that. All ids are trigger-minted (payloads OMIT id). Errors
// surface inline — never a silent success.
// ============================================================================

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { X, ChevronUp, ChevronDown, Star, Eye, EyeOff, Plus, Pencil, Trash2, GitMerge } from 'lucide-react';
import { supabase } from '../../../shared/supabase';
import { Button } from '../../../shared/components';
// Every write below is a helper shared with Ask AI's building card (item 944).
import {
  addExistingContact,
  contactSwapPlan,
  createContactForFacility,
  mergeContactInto,
  patchContactAssignment,
  removeContactAssignment,
  setPrimaryContact,
  swapContactOrder,
} from '../lib/building-saves';
import { SmartSelect } from '../../../components/smart-select';
import { overlayClickClose } from '../../../shared/overlay-close';

const DISPLAY_CAP = 5;

interface AssignmentRow {
  assignment_id: string;
  contact_id: string;
  account_id: string | null;
  facility_id: string | null;
  contact_role: string | null;
  job_title: string | null;
  assignment_email: string | null;
  assignment_mobile: string | null;
  is_displayed: boolean;
  display_order: number | null;
  is_primary: boolean;
  person_name: string;
  master_email: string | null;
  master_mobile: string | null;
}

export interface PocManagerModalProps {
  open: boolean;
  onClose: () => void;
  facility_id: string;
  account_id: string | null;
  onSaved: () => void;
}

export function PocManagerModal({
  open,
  onClose,
  facility_id,
  account_id,
  onSaved,
}: PocManagerModalProps) {
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNewPerson, setShowNewPerson] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data: aData, error: aErr } = await supabase
      .from('contact_assignments')
      .select(
        'assignment_id, contact_id, account_id, facility_id, contact_role, job_title, assignment_email, assignment_mobile, is_displayed, display_order, is_primary',
      )
      .or(
        `facility_id.eq.${facility_id},and(facility_id.is.null,account_id.eq.${account_id ?? '__none__'})`,
      )
      .eq('is_removed', false)
      .order('is_displayed', { ascending: false })
      .order('display_order', { ascending: true, nullsFirst: false });
    if (aErr) {
      setError(aErr.message);
      setLoading(false);
      return;
    }
    const aRows = (aData ?? []) as Array<Record<string, unknown>>;
    const ids = Array.from(
      new Set(aRows.map((r) => r.contact_id as string).filter(Boolean)),
    );
    const mRes = ids.length
      ? await supabase
          .from('contacts')
          .select('contact_id, first_name, last_name, preferred_name, email, mobile')
          .in('contact_id', ids)
      : { data: [] as unknown[] };
    const mMap: Record<string, Record<string, unknown>> = {};
    for (const m of (mRes.data ?? []) as Array<Record<string, unknown>>) {
      mMap[m.contact_id as string] = m;
    }
    const mapped: AssignmentRow[] = aRows.map((a) => {
      const m = mMap[a.contact_id as string] ?? {};
      const nm = (
        (m.preferred_name as string)
          ? `${m.preferred_name} ${m.last_name ?? ''}`
          : `${m.first_name ?? ''} ${m.last_name ?? ''}`
      ).trim();
      return {
        assignment_id: a.assignment_id as string,
        contact_id: a.contact_id as string,
        account_id: (a.account_id as string | null) ?? null,
        facility_id: (a.facility_id as string | null) ?? null,
        contact_role: (a.contact_role as string | null) ?? null,
        job_title: (a.job_title as string | null) ?? null,
        assignment_email: (a.assignment_email as string | null) ?? null,
        assignment_mobile: (a.assignment_mobile as string | null) ?? null,
        is_displayed: (a.is_displayed as boolean | null) ?? false,
        display_order: (a.display_order as number | null) ?? null,
        is_primary: (a.is_primary as boolean | null) ?? false,
        person_name: nm || (m.email as string) || (a.contact_id as string),
        master_email: (m.email as string | null) ?? null,
        master_mobile: (m.mobile as string | null) ?? null,
      };
    });
    setRows(mapped);
    setLoading(false);
  }, [facility_id, account_id]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  if (!open) return null;

  const displayedCount = rows.filter((r) => r.is_displayed).length;
  const overCap = displayedCount > DISPLAY_CAP;

  async function patch(assignmentId: string, fields: Record<string, unknown>): Promise<boolean> {
    setBusyId(assignmentId);
    setError(null);
    const { error: e } = await patchContactAssignment(assignmentId, fields);
    setBusyId(null);
    if (e) {
      setError(e.message);
      return false;
    }
    await load();
    onSaved();
    return true;
  }

  async function setPrimary(r: AssignmentRow) {
    setBusyId(r.assignment_id);
    setError(null);
    // Single primary per scope: clear siblings in the same facility/account first.
    const { error: e } = await setPrimaryContact(r);
    setBusyId(null);
    if (e) {
      setError(e.message);
      return;
    }
    await load();
    onSaved();
  }

  async function move(r: AssignmentRow, dir: -1 | 1) {
    // The swap is shared with Ask AI's "move a contact" card (building-saves.ts).
    const plan = contactSwapPlan(rows, r.assignment_id, dir);
    if (!plan) return;
    setBusyId(r.assignment_id);
    setError(null);
    await swapContactOrder(plan);
    setBusyId(null);
    await load();
    onSaved();
  }

  async function addExistingPerson(contactId: string) {
    setError(null);
    const maxOrder = rows
      .filter((r) => r.facility_id === facility_id)
      .reduce((mx, r) => Math.max(mx, r.display_order ?? 0), 0);
    const { error: e } = await addExistingContact(facility_id, contactId, maxOrder + 1);
    if (e) {
      setError(e.message);
      return;
    }
    await load();
    onSaved();
  }

  // Real "Remove from facility" (distinct from Hide) — audited soft remove via
  // RPC. Reversible (clears is_removed in the DB). Confirm first.
  async function removeAssignment(r: AssignmentRow) {
    if (!window.confirm(`Remove ${r.person_name} from this facility? This is reversible and is logged.`)) return;
    setBusyId(r.assignment_id);
    setError(null);
    const { error: e } = await removeContactAssignment(r.assignment_id);
    setBusyId(null);
    if (e) {
      setError(e.message);
      return;
    }
    await load();
    onSaved();
  }

  // Merge this card's person INTO an existing person (fixes accidental dups).
  async function mergePerson(r: AssignmentRow, targetContactId: string) {
    if (!targetContactId || targetContactId === r.contact_id) return;
    setBusyId(r.assignment_id);
    setError(null);
    const { error: e } = await mergeContactInto(r.contact_id, targetContactId);
    setBusyId(null);
    if (e) {
      setError(e.message);
      return;
    }
    await load();
    onSaved();
  }

  const displayed = rows.filter((r) => r.is_displayed);
  const hidden = rows.filter((r) => !r.is_displayed);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        className="my-8 w-full max-w-2xl rounded-xl border border-tcs-border bg-tcs-raised p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-tcs-text">Manage points of contact</h3>
            <p className="mt-0.5 text-xs text-tcs-text-muted">
              {displayedCount} shown on card · {hidden.length} hidden
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {overCap && (
          <p className="mt-3 rounded bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
            {displayedCount} contacts are set to display — the card shows the first {DISPLAY_CAP}.
            Hide some, or reorder so the most important appear first.
          </p>
        )}
        {error && (
          <p className="mt-3 rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong">{error}</p>
        )}

        {loading ? (
          <p className="mt-4 text-sm text-tcs-text-muted">Loading…</p>
        ) : (
          <div className="mt-4 space-y-4">
            <Section title={`Shown on card (${displayed.length})`}>
              {displayed.length === 0 ? (
                <Empty>No contacts are shown on this card yet.</Empty>
              ) : (
                displayed.map((r, i) => (
                  <RowItem
                    key={r.assignment_id}
                    row={r}
                    rank={i}
                    total={displayed.length}
                    busy={busyId === r.assignment_id}
                    editing={editingId === r.assignment_id}
                    onToggleEdit={() =>
                      setEditingId(editingId === r.assignment_id ? null : r.assignment_id)
                    }
                    onToggleDisplayed={() => patch(r.assignment_id, { is_displayed: !r.is_displayed })}
                    onSetPrimary={() => setPrimary(r)}
                    onMove={(dir) => move(r, dir)}
                    onSaveFields={(f) => patch(r.assignment_id, f).then((ok) => { if (ok) setEditingId(null); })}
                    onRemove={() => removeAssignment(r)}
                    onMerge={(targetId) => mergePerson(r, targetId)}
                  />
                ))
              )}
            </Section>

            {hidden.length > 0 && (
              <Section title={`Hidden (${hidden.length})`}>
                {hidden.map((r) => (
                  <RowItem
                    key={r.assignment_id}
                    row={r}
                    rank={-1}
                    total={0}
                    busy={busyId === r.assignment_id}
                    editing={editingId === r.assignment_id}
                    onToggleEdit={() =>
                      setEditingId(editingId === r.assignment_id ? null : r.assignment_id)
                    }
                    onToggleDisplayed={() => patch(r.assignment_id, { is_displayed: !r.is_displayed })}
                    onSetPrimary={() => setPrimary(r)}
                    onMove={() => undefined}
                    onSaveFields={(f) => patch(r.assignment_id, f).then((ok) => { if (ok) setEditingId(null); })}
                    onRemove={() => removeAssignment(r)}
                    onMerge={(targetId) => mergePerson(r, targetId)}
                  />
                ))}
              </Section>
            )}

            <Section title="Add a contact">
              <div className="space-y-2">
                <SmartSelect
                  entity="contact"
                  value={null}
                  onChange={(id) => { if (id) void addExistingPerson(id); }}
                  onCreateNew={() => setShowNewPerson(true)}
                  placeholder="Search existing people…"
                />
                {!showNewPerson ? (
                  <button
                    type="button"
                    onClick={() => setShowNewPerson(true)}
                    className="flex items-center gap-1 text-xs text-tcs-accent-ink hover:underline"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add a brand-new person
                  </button>
                ) : (
                  <NewPersonForm
                    facility_id={facility_id}
                    account_id={account_id}
                    onCancel={() => setShowNewPerson(false)}
                    onCreated={async () => {
                      setShowNewPerson(false);
                      await load();
                      onSaved();
                    }}
                    onAttachExisting={async (contactId) => {
                      setShowNewPerson(false);
                      await addExistingPerson(contactId);
                    }}
                    onError={setError}
                  />
                )}
              </div>
            </Section>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button variant="primary" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {title}
      </div>
      <div className="mt-2 space-y-2">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-tcs-text-muted">{children}</p>;
}

function RowItem({
  row,
  rank,
  total,
  busy,
  editing,
  onToggleEdit,
  onToggleDisplayed,
  onSetPrimary,
  onMove,
  onSaveFields,
  onRemove,
  onMerge,
}: {
  row: AssignmentRow;
  rank: number;
  total: number;
  busy: boolean;
  editing: boolean;
  onToggleEdit: () => void;
  onToggleDisplayed: () => void;
  onSetPrimary: () => void;
  onMove: (dir: -1 | 1) => void;
  onSaveFields: (fields: Record<string, unknown>) => void;
  onRemove: () => void;
  onMerge: (targetContactId: string) => void;
}) {
  const [role, setRole] = useState(row.contact_role ?? '');
  const [title, setTitle] = useState(row.job_title ?? '');
  const [mobile, setMobile] = useState(row.assignment_mobile ?? '');
  const [email, setEmail] = useState(row.assignment_email ?? '');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (editing) {
      setRole(row.contact_role ?? '');
      setTitle(row.job_title ?? '');
      setMobile(row.assignment_mobile ?? '');
      setEmail(row.assignment_email ?? '');
    }
  }, [editing, row]);

  const phone = row.assignment_mobile ?? row.master_mobile;
  const mail = row.assignment_email ?? row.master_email;
  const scopeLabel = row.facility_id ? 'This facility' : 'Account-wide';

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-surface/30 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-tcs-text">{row.person_name}</span>
            {row.is_primary && (
              <span className="rounded bg-tcs-accent-bg px-1.5 py-0.5 text-[10px] font-semibold text-tcs-text">
                Primary
              </span>
            )}
            <span className="text-[10px] text-tcs-text-muted">{scopeLabel}</span>
          </div>
          <div className="mt-0.5 text-xs text-tcs-text-muted">
            {[row.contact_role, row.job_title].filter(Boolean).join(' · ') || '—'}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-tcs-text-secondary">
            {phone && <span>{phone}</span>}
            {mail && <span className="truncate">{mail}</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {rank >= 0 && total > 1 && (
            <>
              <IconBtn label="Move up" disabled={busy || rank === 0} onClick={() => onMove(-1)}>
                <ChevronUp className="h-4 w-4" />
              </IconBtn>
              <IconBtn label="Move down" disabled={busy || rank === total - 1} onClick={() => onMove(1)}>
                <ChevronDown className="h-4 w-4" />
              </IconBtn>
            </>
          )}
          <IconBtn label="Set primary" active={row.is_primary} disabled={busy} onClick={onSetPrimary}>
            <Star className="h-4 w-4" />
          </IconBtn>
          <IconBtn
            label={row.is_displayed ? 'Hide from card' : 'Show on card'}
            disabled={busy}
            onClick={onToggleDisplayed}
          >
            {row.is_displayed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </IconBtn>
          <IconBtn label="Edit details" active={editing} disabled={busy} onClick={onToggleEdit}>
            <Pencil className="h-4 w-4" />
          </IconBtn>
          <IconBtn
            label="Merge into another person"
            active={merging}
            disabled={busy}
            onClick={() => setMerging((m) => !m)}
          >
            <GitMerge className="h-4 w-4" />
          </IconBtn>
          <IconBtn label="Remove from facility" disabled={busy} onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </IconBtn>
        </div>
      </div>

      {merging && (
        <div className="mt-3 space-y-2 border-t border-tcs-border pt-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
            Merge {row.person_name} into…
          </div>
          <SmartSelect
            entity="contact"
            value={null}
            onChange={(id) => {
              if (id) {
                onMerge(id);
                setMerging(false);
              }
            }}
            placeholder="Pick the person to keep…"
          />
          <p className="text-[11px] text-tcs-text-muted">
            This card&apos;s person is merged into the one you pick; their links move over and this duplicate is hidden. Reversible.
          </p>
          <div className="flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setMerging(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-tcs-border pt-3">
          <MiniInput label="Role" value={role} onChange={setRole} />
          <MiniInput label="Job title" value={title} onChange={setTitle} />
          <MiniInput label="Phone (this site)" value={mobile} onChange={setMobile} />
          <MiniInput label="Email (this site)" value={email} onChange={setEmail} />
          <div className="col-span-2 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onToggleEdit} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() =>
                onSaveFields({
                  contact_role: role.trim() || null,
                  job_title: title.trim() || null,
                  assignment_mobile: mobile.trim() || null,
                  assignment_email: email.trim() || null,
                })
              }
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function IconBtn({
  label,
  children,
  onClick,
  disabled,
  active,
}: {
  label: string;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`rounded p-1 ${
        active ? 'text-tcs-accent-ink' : 'text-tcs-text-secondary'
      } hover:bg-tcs-border hover:text-tcs-text disabled:opacity-30`}
    >
      {children}
    </button>
  );
}

function MiniInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
      />
    </label>
  );
}

interface ContactMatch {
  contact_id: string;
  display_name: string | null;
  phone: string | null;
  email: string | null;
}

function NewPersonForm({
  facility_id,
  account_id,
  onCancel,
  onCreated,
  onAttachExisting,
  onError,
}: {
  facility_id: string;
  account_id: string | null;
  onCancel: () => void;
  onCreated: () => Promise<void> | void;
  onAttachExisting: (contactId: string) => Promise<void> | void;
  onError: (msg: string) => void;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [title, setTitle] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [scope, setScope] = useState<'Facility' | 'Account'>('Facility');
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState<ContactMatch[]>([]);
  const matchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Match-on-create: when a phone/email is entered that already belongs to an
  // existing person, warn and offer to attach that person instead of minting a
  // new (fragment) contact. Debounced; root-cause fix for the 95 fragments.
  useEffect(() => {
    if (matchTimer.current) clearTimeout(matchTimer.current);
    const phone = mobile.trim();
    const mail = email.trim();
    if (!phone && !mail) {
      setMatches([]);
      return;
    }
    matchTimer.current = setTimeout(async () => {
      const { data } = await supabase.rpc('find_contact_matches', {
        p_phone: phone || null,
        p_email: mail || null,
      });
      setMatches(((data ?? []) as ContactMatch[]).slice(0, 5));
    }, 400);
    return () => {
      if (matchTimer.current) clearTimeout(matchTimer.current);
    };
  }, [mobile, email]);

  async function submit() {
    if (!first.trim() && !last.trim() && !email.trim()) {
      onError('Enter at least a name or an email for the new person.');
      return;
    }
    setBusy(true);
    onError('');
    // The new person and their row here, in one helper shared with Ask AI's
    // building card (lib/building-saves.ts).
    const res = await createContactForFacility({
      first, last, title, mobile, email, role, scope,
      facilityId: facility_id,
      accountId: account_id,
    });
    setBusy(false);
    if (res.error) {
      onError(res.error);
      return;
    }
    await onCreated();
  }

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-surface/30 p-3">
      <div className="grid grid-cols-2 gap-2">
        <MiniInput label="First name" value={first} onChange={setFirst} />
        <MiniInput label="Last name" value={last} onChange={setLast} />
        <MiniInput label="Job title" value={title} onChange={setTitle} />
        <MiniInput label="Role" value={role} onChange={setRole} />
        <MiniInput label="Phone" value={mobile} onChange={setMobile} />
        <MiniInput label="Email" value={email} onChange={setEmail} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
          Scope
        </span>
        <button
          type="button"
          onClick={() => setScope('Facility')}
          className={`rounded border px-2 py-1 text-xs ${
            scope === 'Facility'
              ? 'border-tcs-accent bg-tcs-accent-bg text-tcs-text'
              : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-surface'
          }`}
        >
          This facility
        </button>
        <button
          type="button"
          disabled={!account_id}
          onClick={() => setScope('Account')}
          className={`rounded border px-2 py-1 text-xs disabled:opacity-40 ${
            scope === 'Account'
              ? 'border-tcs-accent bg-tcs-accent-bg text-tcs-text'
              : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-surface'
          }`}
        >
          Account-wide
        </button>
      </div>
      {matches.length > 0 && (
        <div className="mt-3 rounded border border-st-warn-border-strong bg-st-warn-bg p-3">
          <p className="text-xs font-semibold text-st-warn-fg-strong">
            This phone/email already belongs to {matches.length === 1 ? 'someone' : 'people'} on file.
            Attach the existing person instead of creating a duplicate?
          </p>
          <div className="mt-2 space-y-1">
            {matches.map((m) => (
              <div key={m.contact_id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-xs text-st-warn-fg-strong">
                  {m.display_name || m.contact_id}
                  {m.phone ? ` · ${m.phone}` : ''}
                  {m.email ? ` · ${m.email}` : ''}
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onAttachExisting(m.contact_id)}
                  className="shrink-0 rounded border border-st-warn-border-strong bg-tcs-raised px-2 py-0.5 text-[11px] font-medium text-st-warn-fg-strong hover:bg-st-warn-bg-strong disabled:opacity-40"
                >
                  Attach this person
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={submit} disabled={busy}>
          {busy ? 'Creating…' : 'Create & add'}
        </Button>
      </div>
    </div>
  );
}
